// src/Dashboard/pages/user/News/hooks/useNewsDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useUser } from "../../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../../utils/roleHelper";

import {
  createNews,
  deleteNews,
  getNews,
} from "../../../../../services/newsServices";

import {
  DEFAULT_NEWS_UI,
  DEFAULT_VISIBLE_NEWS,
  NEWS_UI_STORAGE_KEY,
  collatorFR,
  getNewsDate,
  getNewsSearchText,
  readStorageJson,
  writeStorageJson,
} from "../utils/newsDashboardUtils";

export function useNewsDashboard() {
  const { user } = useUser();

  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ui, setUi] = useState(() => {
    const saved = readStorageJson(NEWS_UI_STORAGE_KEY, null);

    return saved ? { ...DEFAULT_NEWS_UI, ...saved } : DEFAULT_NEWS_UI;
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search || "");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create"); // create | edit
  const [currentNews, setCurrentNews] = useState(null);

  const [readDrawerOpen, setReadDrawerOpen] = useState(false);
  const [readNews, setReadNews] = useState(null);

  const persistUi = useCallback((next) => {
    setUi(next);
    writeStorageJson(NEWS_UI_STORAGE_KEY, next);
  }, []);

  const updateUi = useCallback(
    (patch) => {
      const shouldResetPage = ["search", "showInactive", "sort"].some((key) =>
        Object.prototype.hasOwnProperty.call(patch, key)
      );

      persistUi({
        ...ui,
        ...patch,
        perPage: shouldResetPage
          ? DEFAULT_VISIBLE_NEWS
          : patch.perPage ?? ui.perPage,
      });
    },
    [persistUi, ui]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      updateUi({
        search: debouncedSearch,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedSearch]); // volontairement limité

  const loadNews = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getNews();

      setNewsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement actualités :", error);
      setNewsList([]);
      toast.error("Impossible de charger les actualités.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const filteredNews = useMemo(() => {
    const q = String(ui.search || "").trim().toLowerCase();

    let list = newsList.filter((news) => {
      if (!isManager) return !!news?.is_active;

      if (ui.showInactive) return true;

      return !!news?.is_active;
    });

    if (q) {
      list = list.filter((news) => getNewsSearchText(news).includes(q));
    }

    list.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(
          String(a?.title || ""),
          String(b?.title || "")
        );
      }

      const da = new Date(getNewsDate(a) || 0).getTime();
      const db = new Date(getNewsDate(b) || 0).getTime();

      if (ui.sort === "dateAsc") {
        return da - db;
      }

      return db - da;
    });

    return list;
  }, [newsList, ui, isManager]);

  const visibleNews = useMemo(() => {
    return filteredNews.slice(0, ui.perPage || DEFAULT_VISIBLE_NEWS);
  }, [filteredNews, ui.perPage]);

  const stats = useMemo(() => {
    const total = newsList.length;
    const active = newsList.filter((news) => !!news?.is_active).length;
    const inactive = total - active;
    const withImage = newsList.filter((news) => !!news?.image_url).length;

    return {
      total,
      active,
      inactive,
      withImage,
      filtered: filteredNews.length,
    };
  }, [newsList, filteredNews.length]);

  const resetFilters = useCallback(() => {
    setDebouncedSearch("");
    persistUi(DEFAULT_NEWS_UI);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    persistUi({
      ...ui,
      perPage: (ui.perPage || DEFAULT_VISIBLE_NEWS) + DEFAULT_VISIBLE_NEWS,
    });
  }, [persistUi, ui]);

  const openCreateDrawer = useCallback(async () => {
    if (!isManager) return;

    if (!user?.userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }

    try {
      const draft = await createNews({
        title: "",
        content: "",
        image_url: "",
        author_id: user.userId,
        is_active: true,
      });

      setCurrentNews(draft);
      setDrawerMode("create");
      setDrawerOpen(true);
    } catch (error) {
      console.error("Erreur création brouillon actualité :", error);
      toast.error("Erreur lors de la création de l’actualité.");
    }
  }, [isManager, user?.userId]);

  const openEditDrawer = useCallback((news) => {
    if (!isManager) return;

    setCurrentNews(news);
    setDrawerMode("edit");
    setDrawerOpen(true);
  }, [isManager]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setCurrentNews(null);
    setDrawerMode("create");
  }, []);

  const handleSaved = useCallback(async () => {
    await loadNews();
    closeDrawer();
  }, [loadNews, closeDrawer]);

  const openReadDrawer = useCallback((news) => {
    setReadNews(news);
    setReadDrawerOpen(true);
  }, []);

  const closeReadDrawer = useCallback(() => {
    setReadNews(null);
    setReadDrawerOpen(false);
  }, []);

  const handleDeleteNews = useCallback(
    async (news) => {
      if (!isManager || !news?.id) return;

      const ok = window.confirm(
        `Supprimer définitivement l’actualité "${
          news?.title || "Sans titre"
        }" ?`
      );

      if (!ok) return;

      try {
        await deleteNews(news.id);
        toast.success("Actualité supprimée.");
        await loadNews();
      } catch (error) {
        console.error("Erreur suppression actualité :", error);
        toast.error("Erreur lors de la suppression.");
      }
    },
    [isManager, loadNews]
  );

  return {
    user,
    userRole,
    isManager,

    newsList,
    filteredNews,
    visibleNews,
    loading,
    stats,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    drawerOpen,
    drawerMode,
    currentNews,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleSaved,

    readDrawerOpen,
    readNews,
    openReadDrawer,
    closeReadDrawer,

    handleDeleteNews,
    refresh: loadNews,
  };
}